/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Avatar from '@material-ui/core/Avatar';

import ApiCommentsTable from './ApiCommentsTable';
import CopyTextField from '../../../elements/CopyTextField';
import GreyButton from '../../../elements/GreyButton';
import PrimaryButton from '../../../elements/PrimaryButton';
import TextButton from '../../../elements/TextButton';

import {
  isValidData,
  renderSkeleton,
  getAllApiGatewayUrls,
  localStoreOperation,
  isGraphqlApi,
  isThirdPartyApi,
  getEndpointUrls,
  getVisibilityFromAccessibilityType,
  getAPIType,
} from '../../../data/utils';
import {
  fetchGraphqlResponse,
  postComment,
} from '../../../data/operations';
import {
  ApiCommentsReducer,
  ApiReducer,
  UserReducer,
  ApplicationState,
  Api,
  Comments,
  CreateComment,
  GraphqlReducer,
  EndpointInfo,
  StorageOperations,
  Environment,
  EnvConfigReducer,
} from "../../../data/interfaces";
import {
  CLAIMS_FULL_NAME,
  CREATED_BY_USER,
  PATH_SEPARATOR,
  MAX_COMMENT_LENGTH,
  CORRELATION_ID,
  API_TYPE_WS,
  API_TYPE_GRAPHQL,
} from '../../../data/constants';
import { Box, MenuItem, Select } from '@material-ui/core';
import Settings from '../../../conf/settings';
import {
  trackOverviewAddComment,
  trackOverviewPageLoad,
} from '../../../util/app-insights';
import ApiResources from '../ApiResources';
import Notification from '../../ChoreoSystem/Notification/Notification';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  apiIconImage: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiIconImage,
    color: theme.palette.getContrastText(
      theme.custom.apiOverviewPage.tabs.overview.apiIconImage.backgroundColor
    ),
    textTransform: 'capitalize',
  },
  apiIconImageBg: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiIconImageBackground,
  },
  gridText: {
    textAlign: 'left',
    marginTop: '10px',
  },
  apiName: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiName,
  },
  apiVersionProvider: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiVersionProvider,
  },
  apiInfoTitle: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '97px',
    marginRight: '16px',
  },
  apiInfoTitle2: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  apiInfoTitleHead: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiDetailTitles,
  },
  apiOverviewContent: {
    ...theme.custom.apiOverviewPage.tabs.overview.apiDetailsContent,
  },
  apiEndpointWidth: {
    maxWidth: '100%',
    fontSize: '13px',
    lineHeight: '24px',
  },
  hideInput: {
    '& .MuiInputBase-input': {
      opacity: 0,
    },
  },
  endpointContainer: {
    position: 'relative',
  },
  epSelectionContainer: {
    zIndex:0,
    display: 'inline-flex',
    width: '100%',
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  endpointUrlSelection: {
    width: '100%',
    background: '#f7f8fb',
    outline: 'none',
    border: 'none',
    color: '#878787',
    font: 'inherit',
    fontSize: '14px',
    letterSpacing: '0.75px',
    paddingRight: theme.spacing(0.25),
    marginRight: theme.spacing(8),
  },
  envBox: {
    display: 'flex',
    alignItems: 'center',
  },
  envValue: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  envChip: {
    minWidth: '100px',
    border: '1px solid #5567d5',
    borderRadius: '15px',
    background: '#5567d5',
    color: '#ffffff',
    height: '25px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    marginRight: theme.spacing(1),
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  envChipSecondary: {
    border: "1px solid #5567d5",
    borderRadius: "15px",
    background: "#f0f1fb",
    color: "#5567d5",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    marginRight: theme.spacing(1),
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1.2,
    padding: "0 8px",
  },

  selectBg: {
    '& .MuiSelect-select': {
      backgroundColor: '#f7f8fb',
      marginLeft: theme.spacing(0.5),
      textAlign: 'left',
    },
  },
  envNameStyles: {
    fontWeight: "bold",
    paddingRight: "5px",
  },
  lifecycleStatus: {
    ...theme.custom.elements.apiCard.lifecycleStatus,
    boxSizing: "border-box",
    border: "1px solid #8D91A3",
    fontSize: "10px",
    backgroundColor: "#E0E2E9",
    padding: "2px 10px",
    borderRadius: "5px",
    marginLeft: theme.spacing(1),
  },
}));

interface ApiInformationProps {
  organizationHandle: string;
  apiReducer: ApiReducer;
  commentsReducer: ApiCommentsReducer;
  graphQLReducer: GraphqlReducer;
  userReducer: UserReducer;
  postComment: (
    apiId: string,
    organizationId: string,
    comment: CreateComment
  ) => Promise<void>;
  graphqlResponse: GraphqlReducer;
  dispatchFetchGraphqlResponse: () => void;
  setSelectedEnviornment: (environment?: Environment) => void;
}

function ApiInformation(props: ApiInformationProps) {
  const {
    organizationHandle,
    apiReducer,
    commentsReducer,
    userReducer,
    postComment,
    graphqlResponse,
    dispatchFetchGraphqlResponse,
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const { enableInV2 } = Settings;
  const {
    loading: apiLoading,
    api,
    apiThumbnail,
    apiThumbnailLoading,
    majorVersionApiList,
    majorVersionApisLoading,
  } = apiReducer;
  const { comments } = commentsReducer;
  const {
    apimTokenObtained,
    userInfo,
    orgs: userOrganizations,
    selectedOrg,
  } = userReducer;
  const [addCommentHidden, setAddCommentHidden] = useState(true);
  const [formValues, setFormValues] = useState({ newComment: '' });
  const [apiState, setApiState] = useState<{
    api: Api;
    thumbnail: string;
    comments: Comments;
  }>({ api: api, thumbnail: '', comments: comments });
  const { graphqlInfo, loading: isGraphqlResponseFetching } = graphqlResponse;

  const apiCommentCount = apiState.comments.count;
  const apiName = apiState.api?.displayName;
  const apiContext = apiState.api?.context;
  const apiVersion = apiState.api?.version;
  const isLatestMajorRangeVersion = apiState.api?.versionInfo?.majorRange?.isLatest || false;
  const apiOwner =
    apiState.api?.businessInformation?.businessOwner ||
    apiState.api.businessInformation?.technicalOwner;
  const apiOverview = apiState.api?.description;
  const [endpointUrls, setEndpointUrls] = useState<EndpointInfo[]>(
    [] as EndpointInfo[]
  );
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");
  const [selectedEnv, setSelectedEnv] = useState<string>("");
  const [selectedEnvironmentName, setSelectedEnvironmentName] = useState<string>("");
  const [selectedVisibility, setSelectedVisibility] = useState<string>("");
  const [isSelectedEpSandbox, setIsSelectedEpSandbox] =
    useState<boolean>(false);

  const environments = graphqlInfo?.environments || [];

  const enableCustomDomainView = enableInV2 && apimTokenObtained;

  const externalAPIEndpointURL = apiState.api?.advertiseInfo?.apiExternalProductionEndpoint || "";
  const externalAPISandboxURL = apiState.api?.advertiseInfo?.apiExternalSandboxEndpoint || "";

  useEffect(() => {
    if (enableCustomDomainView && !!api.id) {
      dispatchFetchGraphqlResponse();
    }
  }, [api]);

  useEffect(() => {
    if (isValidData(api)) {
      setApiState((prevState) => {
        return {
          ...prevState,
          api: api,
        };
      });
      if(api && !apiLoading) {
        trackOverviewPageLoad(api?.name, api?.id, localStoreOperation(StorageOperations.GET, CORRELATION_ID) || '');
      }
    }
  }, [api]);

  useEffect(() => {
    if (api && getEndpointUrls(api)?.length === 0) return;

    const customEndpoints: any = [];

    if (isThirdPartyApi(api)) {
      customEndpoints.push({
        url: externalAPIEndpointURL,
        environment: "Backend",
        environmentDisplayName: "Backend",
      });
      if (externalAPISandboxURL) {
        customEndpoints.push({
          url: externalAPISandboxURL,
          environment: "Sandbox",
          environmentDisplayName: "Sandbox",
        });
      }
    }

    const defaultUrls = getAllApiGatewayUrls(
      api ? getEndpointUrls(api) : [],
      graphqlInfo,
      (api.versionInfo && api.versionInfo?.majorRange.isLatest) || false,
      api.version,
      !apimTokenObtained,
      selectedOrg
    );

    let endpointUrls: EndpointInfo[];

    if (customEndpoints.length > 0) {
      if (isThirdPartyApi(api)) {
        endpointUrls = customEndpoints;
      } else {
      endpointUrls = [... new Set(defaultUrls.map((url) => url.environmentDisplayName))]
        .map((env) => {
          const customUrls = customEndpoints
            .filter((endpoint: EndpointInfo) => endpoint.environmentDisplayName === env);
          if (customUrls.length > 0) {
            return [...customUrls];
          } else {
            const defaultUrl = defaultUrls
              .filter((endpoint: EndpointInfo) => endpoint.environmentDisplayName === env);
            return [...defaultUrl];
          }
        }).flat();
      }
    } else {
      endpointUrls = defaultUrls;
    }

    const defaultSelection = endpointUrls?.length > 0 ? endpointUrls[0] : null;
    setEndpointUrls(endpointUrls);
    setSelectedEndpoint(defaultSelection?.url || "");
    setSelectedEnv(defaultSelection?.choreoEnvironmentName || "");
    setSelectedEnvironmentName(defaultSelection?.environment || "");
    setSelectedVisibility(
      getVisibilityFromAccessibilityType(
        defaultSelection?.accessibilityType || ""
      )
    );
    setIsSelectedEpSandbox(defaultSelection?.isSandbox && defaultSelection.isCritical || false);
  }, [graphqlInfo, api, majorVersionApiList, majorVersionApisLoading]);

  useEffect(() => {
    if (apiThumbnail) {
      setApiState((prevState) => {
        return {
          ...prevState,
          thumbnail: URL.createObjectURL(apiThumbnail),
        };
      });
    }
  }, [apiThumbnail]);

  useEffect(() => {
    if (isValidData(comments)) {
      setApiState((prevState) => {
        return {
          ...prevState,
          comments: comments,
        };
      });
    }
  }, [comments]);

  const handleCommentClick = () => {
    setFormValues({ newComment: '' });
    setAddCommentHidden((prevState) => !prevState);
  };

  const handleFormChange = (event: any) => {
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value,
    });
  };

  const handleAddCommentFormSubmit = (event: any) => {
    event.preventDefault();
    const fullName = userInfo[CLAIMS_FULL_NAME] || 'Anonymous';
    const data: CreateComment = {
      // @TODO Appending the full name of the commentor as a temporary fix to overcome UUID username.
      content: formValues.newComment + CREATED_BY_USER + fullName,
    };
    if (api.id && selectedOrg.uuid) {
      postComment(api.id, selectedOrg.uuid, data);
      trackOverviewAddComment(api.id, formValues.newComment);
    }
    setFormValues({ newComment: '' });
    setAddCommentHidden(true);
  };

  const ApiOwner = (owner: string) => {
    if (owner) {
      return intl.formatMessage(
        {
          id: 'api.apiOverview.apiInformation.api.ownership',
          defaultMessage: 'by {owner}',
        },
        { owner }
      );
    } else {
      return intl.formatMessage({
        id: 'api.apiOverview.apiInformation.api.ownership.noOwner',
        defaultMessage: 'No owner available.',
      });
    }
  };

  const setSelectedUrl = (event: any) => {
    setGwEnvUrl(event?.target?.value);
  };

  const setGwEnvUrl = (value: string) => {
    const selectedEnvObject = endpointUrls?.find((endpoint) => {
      return endpoint?.url === value;
    });
    setSelectedEndpoint(selectedEnvObject?.url || "");
    setSelectedEnv(selectedEnvObject?.choreoEnvironmentName || "");
    setSelectedEnvironmentName(selectedEnvObject?.environment || "");
    setSelectedVisibility(
      getVisibilityFromAccessibilityType(
        selectedEnvObject?.accessibilityType || ""
      )
    );
    setIsSelectedEpSandbox(selectedEnvObject?.isSandbox && selectedEnvObject.isCritical || false);
  };

  const renderApiTypeMessage = (api?: Api) => {
    if (getAPIType(api) === API_TYPE_WS) {
      return (
        <FormattedMessage
          id="components.api.ApiOverview.ApiInformation.apitype.websocket"
          defaultMessage="WebSocket"
        />
      );
    } else if (getAPIType(api) === API_TYPE_GRAPHQL) {
      return (
        <FormattedMessage
          id="components.api.ApiOverview.ApiInformation.apitype.graphQL"
          defaultMessage="GraphQL"
        />
      );
    } else {
      return (
        <FormattedMessage
          id="components.api.ApiOverview.ApiInformation.apitype.rest"
          defaultMessage="REST"
        />
      );
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        {apiLoading || apiThumbnailLoading || !apiThumbnail ? (
          <span style={{ padding: '8px' }}>
            {renderSkeleton('rect', 97, 97)}
          </span>
        ) : (
          <Grid item>
            <Avatar
              className={classes.apiIconImage}
              classes={{ img: classes.apiIconImageBg }}
              alt={apiName}
              src={apiState.thumbnail}
              variant='square'
            />
          </Grid>
        )}
        <Grid item xs={12} sm container className={classes.gridText}>
          <Grid item xs container direction="column" spacing={2}>
            <Grid item xs lg={12}>
              {apiLoading ? (
                renderSkeleton("rect", "50%", 24)
              ) : (
                  <>
                    <Typography
                      variant="h5"
                      className={classes.apiVersionProvider}
                      data-testid="txt-api-type"
                    >
                      {renderApiTypeMessage(api)}
                      {api.lifeCycleStatus === 'PROTOTYPED' && (
                        <span className={classes.lifecycleStatus}>
                          <FormattedMessage
                            id='api.apiList.apiCard.labels.lifecycleStatus.preRelease'
                            defaultMessage='Pre-Release'
                          />
                        </span>
                      )}
                    </Typography>
                    <Typography
                      className={classes.apiName}
                      data-testid='txt-api-name'
                    >
                      {apiName}
                    </Typography>
                  </>
              )}

              {apiLoading ? (
                renderSkeleton('text', '50%', 20)
              ) : (
                <Typography className={classes.apiVersionProvider}>
                  <span data-testid='txt-api-version'>
                    <FormattedMessage
                      id='api.apiOverview.apiInformation.api.version'
                      defaultMessage='Version {apiVersion}'
                      values={{
                        apiVersion: Settings.intelligentRoutingFeatureEnabled && isLatestMajorRangeVersion
                        ? apiVersion.split(".")[0]
                        : apiVersion}}
                    />
                  </span>
                  <span>&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;</span>
                  <span data-testid='txt-api-owner'>{ApiOwner(apiOwner)}</span>
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
        {isThirdPartyApi(api) ? (
          <Grid
            item
            container
            direction='row'
            lg={12}
            style={{ paddingTop: '30px' }}
          >
            <Grid item className={classes.apiInfoTitle}>
              <Typography
                className={classes.apiInfoTitleHead}
                data-testid='txt-api-endpoint'
              >
                {externalAPISandboxURL ? (
                  <FormattedMessage
                    id='api.apiOverview.apiInformation.apiEndpoints'
                    defaultMessage='Endpoint(s)'
                   />
                ) : (
                  <FormattedMessage
                    id='api.apiOverview.apiInformation.apiEndpoint'
                    defaultMessage='Endpoint'
                  />
                )}
              </Typography>
            </Grid>
            <Grid item xs={9} lg={9} md={9} sm={9}>
              {apiLoading ? (
                renderSkeleton('rect', 340, 48)
              ) : (
                <div className={classes.endpointContainer}>
                  <CopyTextField
                    data-testid="input-endpoint-copy"
                    textContent={
                      externalAPISandboxURL
                        ? selectedEndpoint
                        : externalAPIEndpointURL
                    }
                    disabled={true}
                    className={clsx(
                      classes.apiEndpointWidth,
                      externalAPISandboxURL && classes.hideInput
                    )}
                  />
                  <div className={classes.epSelectionContainer}>
                    <FormControl className={classes.endpointUrlSelection}>
                      {endpointUrls &&
                        endpointUrls?.length > 0 &&
                        selectedEndpoint && (
                          <Select
                            disableUnderline
                            value={selectedEndpoint}
                            onChange={setSelectedUrl}
                            className={classes.selectBg}
                            renderValue={(value: any) => (
                              <Box className={classes.envBox}>
                                {selectedEnv && (
                                  <Box
                                    className={classes.envNameStyles}
                                    data-cyid={`endpoint-environment-${selectedEnv}`}
                                  >
                                    {selectedEnv} :
                                  </Box>
                                )}
                                {selectedVisibility && (
                                  <Box
                                    className={classes.envChipSecondary}
                                    data-cyid={`endpoint-environment-${selectedEnv}`}
                                  >
                                    {selectedVisibility}
                                  </Box>
                                )}
                                {isSelectedEpSandbox && (
                                  <Box
                                    className={classes.envChipSecondary}
                                    data-cyid={`endpoint-environment-${selectedEnv}`}
                                  >
                                    Sandbox
                                  </Box>
                                )}
                                <Box className={classes.envValue}>{value}</Box>
                              </Box>
                            )}
                          >
                            {endpointUrls?.map((endpoint: EndpointInfo) => {
                              return (
                                <MenuItem
                                  key={endpoint.environment}
                                  value={endpoint.url}
                                  data-cyid={`endpoint-list-item-${endpoint.environmentDisplayName}`}
                                >
                                  <Box className={classes.envBox}>
                                    <Box className={classes.envNameStyles}>
                                      {endpoint.choreoEnvironmentName} :
                                    </Box>
                                    {endpoint.accessibilityType && (
                                      <Box className={classes.envChipSecondary}>
                                        {getVisibilityFromAccessibilityType(
                                          endpoint.accessibilityType
                                        )}
                                      </Box>
                                    )}
                                    {endpoint.isSandbox && endpoint.isCritical && (
                                      <Box className={classes.envChipSecondary}>
                                        {"Sandbox"}
                                      </Box>
                                    )}
                                    <Box className={classes.envValue}>
                                      {endpoint.url}
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                    </FormControl>
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        ) : (
          <>
            {api && getEndpointUrls(api)?.length > 0 ? (
              <Grid
                item
                container
                direction="row"
                lg={12}
                style={{ paddingTop: "30px" }}
              >
                <Grid item className={classes.apiInfoTitle}>
                  <Typography
                    className={classes.apiInfoTitleHead}
                    data-testid="txt-api-endpoint"
                  >
                    {enableCustomDomainView ? (
                      <FormattedMessage
                        id="api.apiOverview.apiInformation.apiEndpoints"
                        defaultMessage="Endpoint(s)"
                      />
                    ) : (
                      <FormattedMessage
                        id="api.apiOverview.apiInformation.apiEndpoint"
                        defaultMessage="Endpoint"
                      />
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={9} lg={9} md={9} sm={9}>
                  {apiLoading ? (
                    renderSkeleton("rect", 340, 48)
                  ) : (
                    <div className={classes.endpointContainer}>
                      <CopyTextField
                        data-testid="input-endpoint-copy"
                        textContent={selectedEndpoint}
                        disabled={true}
                        className={clsx(
                          classes.apiEndpointWidth,
                          enableCustomDomainView && classes.hideInput
                        )}
                      />
                      {enableCustomDomainView && (
                        <div className={classes.epSelectionContainer}>
                          {/* TODO (Shanakama) folowing selection will be chnaged in near future with the new design changes */}
                          <FormControl className={classes.endpointUrlSelection}>
                            {endpointUrls &&
                              endpointUrls?.length > 0 &&
                              selectedEndpoint && (
                                <Select
                                  disableUnderline
                                  value={selectedEndpoint}
                                  onChange={setSelectedUrl}
                                  className={classes.selectBg}
                                  renderValue={(value: any) => (
                                    <Box className={classes.envBox}>
                                      {selectedEnv && (
                                        <Box
                                          className={classes.envNameStyles}
                                          data-cyid={`endpoint-environment-${selectedEnv}`}
                                        >
                                          {selectedEnv} :
                                        </Box>
                                      )}
                                      {selectedVisibility && (
                                        <Box
                                          className={classes.envChipSecondary}
                                          data-cyid={`endpoint-environment-${selectedEnv}`}
                                        >
                                          {selectedVisibility}
                                        </Box>
                                      )}
                                      {isSelectedEpSandbox && (
                                        <Box
                                          className={classes.envChipSecondary}
                                          data-cyid={`endpoint-environment-${selectedEnv}`}
                                        >
                                          Sandbox
                                        </Box>
                                      )}
                                      <Box className={classes.envValue}>
                                        {value}
                                      </Box>
                                    </Box>
                                  )}
                                >
                                  {endpointUrls?.map(
                                    (endpoint: EndpointInfo) => {
                                      return (
                                        <MenuItem
                                          key={endpoint.environment}
                                          value={endpoint.url}
                                          data-cyid={`endpoint-list-item-${endpoint.environmentDisplayName}`}
                                        >
                                          <Box className={classes.envBox}>
                                            <Box className={classes.envNameStyles}>
                                              {endpoint.choreoEnvironmentName} :
                                            </Box>

                                            {endpoint.accessibilityType && (
                                              <Box className={classes.envChipSecondary}>
                                                {getVisibilityFromAccessibilityType(
                                                  endpoint.accessibilityType
                                                )}
                                              </Box>
                                            )}
                                            {endpoint.isSandbox && endpoint.isCritical && (
                                              <Box className={classes.envChipSecondary}>
                                                {"Sandbox"}
                                              </Box>
                                            )}
                                            <Box className={classes.envValue}>
                                              {endpoint.url}
                                            </Box>
                                          </Box>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                )}
                            </FormControl>
                          </div>
                        )}
                      </div>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Grid item container style={{ paddingTop: '30px' }}>
                  {apiLoading ? (
                    renderSkeleton('rect', 616, 60)
                  ) : (
                    <Notification color='warning' icon='warning'>
                      <Typography variant='body2'>
                        <FormattedMessage
                          id='api.apiOverview.apiInformation.endpoint.unavailable.warning'
                          defaultMessage={'Endpoint URL(s) are unavailable. '
                            + 'Contact the API publisher to set up endpoint URLs and deploy the API.'}
                        />
                      </Typography>
                    </Notification>
                  )}
                </Grid>
              )}
            </>
          )}
        
        <Grid
          item
          container
          direction='row'
          lg={12}
          style={{ paddingTop: '30px' }}
        >
          <Grid item className={classes.apiInfoTitle}>
            <Typography className={classes.apiInfoTitleHead}>
              <FormattedMessage
                id='api.apiOverview.apiInformation.description.title'
                defaultMessage='Description'
              />
            </Typography>
          </Grid>
          <Grid item xs={9} lg={9} md={9} sm={9}>
            <Typography className={classes.apiOverviewContent}>
              {apiLoading
                ? renderSkeleton('rect', '100%', 48)
                : apiOverview || (
                    <i>
                      <FormattedMessage
                        id='api.apiOverview.apiInformation.messages.noDescription'
                        defaultMessage='No description available'
                      />
                    </i>
                  )}
            </Typography>
          </Grid>
        </Grid>
        {!apiLoading && !isGraphqlApi(api) && (
          <Grid item xs={12} md={12} lg={12}>
            <Grid item xs={12} lg={12} className={classes.apiInfoTitle}>
              {getAPIType(api) === API_TYPE_WS ? (
                <Typography className={classes.apiInfoTitleHead}>
                  <FormattedMessage
                    id="api.apiOverview.apiInformation.channels.title"
                    defaultMessage="Channels"
                  />
                </Typography>
              ) : (
                <Typography className={classes.apiInfoTitleHead}>
                  <FormattedMessage
                    id="api.apiOverview.apiInformation.resources.title"
                    defaultMessage="Resources"
                  />
                </Typography>
              )}
            </Grid>
            <Box pt={1}>
              <ApiResources
                organizationHandle={organizationHandle}
                selectedEnvironment={selectedEnvironmentName}
              />
            </Box>
          </Grid>
        )}
        <Grid item container lg={12} style={{ paddingTop: '30px' }}>
          <Grid item xs lg={12} className={classes.apiInfoTitle2}>
            <Typography
              className={classes.apiInfoTitleHead}
              data-testid='txt-comments-count'
            >
              <FormattedMessage
                id='api.apiOverview.apiInformation.labels.comments'
                defaultMessage='Comments ({apiCommentCount})'
                values={{ apiCommentCount }}
              />
            </Typography>
            {apimTokenObtained && (
              <Typography
                className={classes.apiInfoTitleHead}
                data-testid='btn-add-comment-open-close'
              >
                <TextButton
                  buttonLabel={
                    addCommentHidden
                      ? intl.formatMessage({
                          id: 'api.apiOverview.apiInformation.comments.buttons.addNewComment',
                          defaultMessage: 'Add Comment',
                        })
                      : intl.formatMessage({
                          id: 'api.apiOverview.apiInformation.comments.buttons.close',
                          defaultMessage: 'Close',
                        })
                  }
                  onClick={handleCommentClick}
                />
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            style={{ textAlign: 'right', paddingRight: '8px' }}
            hidden={addCommentHidden}
          >
            <form onSubmit={handleAddCommentFormSubmit}>
              <TextField
                data-testid='input-comment-box'
                id='outlined-full-width'
                placeholder={intl.formatMessage({
                  id: 'api.apiOverview.apiInformation.comments.textField.placeholder',
                  defaultMessage: 'Leave your comment here',
                })}
                fullWidth
                margin='normal'
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  maxLength: MAX_COMMENT_LENGTH,
                }}
                helperText={
                  formValues.newComment.length +
                  PATH_SEPARATOR +
                  MAX_COMMENT_LENGTH
                }
                variant='outlined'
                multiline
                rows={3}
                name='newComment'
                onChange={handleFormChange}
                value={formValues.newComment}
              />
              <GreyButton
                data-testid='btn-cancel-comment'
                onClick={handleCommentClick}
                buttonLabel={intl.formatMessage({
                  id: 'api.apiOverview.apiInformation.comments.buttons.cancel',
                  defaultMessage: 'Cancel',
                })}
              />
              <PrimaryButton
                dataTestId='btn-add-comment'
                buttonLabel={intl.formatMessage({
                  id: 'api.apiOverview.apiInformation.comments.buttons.addComment',
                  defaultMessage: 'Add Comment',
                })}
                type='submit'
              />
            </form>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <ApiCommentsTable />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    apiReducer: state.apiReducer,
    commentsReducer: state.apiCommentsReducer,
    userReducer: state.userReducer,
    graphqlResponse: state.graphqlReducer,
  };
};

const mapDispatchToProps = {
  postComment: postComment,
  dispatchFetchGraphqlResponse: fetchGraphqlResponse,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiInformation);

